/**
 * Defines helper functions
 *
 * A collection of helpful functions that may be imported into any js partial.
 *
 * @package ppd
 * @since   1.0.0
 */

/**
 * Imports jQuery.
 *
 * @see http://api.jquery.com/
 *
 * @since 1.0.0
 */
import $ from 'jquery';

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 *
 * @since 1.0.0
 *
 * @param  {Function} 	func      	Function to debounce
 * @param  {Number} 	wait      	Time to wait in milliseconds
 * @param  {Boolean} 	immediate 	If true is passed, trigger the function on the leading edge, instead of the trailing.
 * @return {Function}
 */
export function debounce(func, wait = 100, immediate = true) {
	let timeout;
	return function () {
		const context = this;
		const args = arguments;
		const later = function () {
			timeout = null;
			if (!immediate) {
				func.apply(context, args);
			}
		};
		const callNow = immediate && !timeout;
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
		if (callNow) {
			func.apply(context, args);
		}
	};
}

/**
 * Append a hash to the current URL.
 *
 * @since 1.0.0
 *
 * @param {String} hash
 */
export function addHashToURL(hash) {
	if (window.history.pushState) {
		const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${hash}`;
		window.history.pushState(
			{
				path: newurl,
			},
			'',
			newurl,
		);
	}
}

/**
 * Scroll the document to the provided element.
 *
 * @since 1.0.0
 *
 * @param  {Object} 	el     	jQuery object
 * @param  {Integer} 	offset 	Offset from the top of the viewport
 */
export function scrollToEl(el, offset) {
	$('html, body').animate(
		{
			scrollTop: el.offset().top - offset,
		},
		500,
	);
}

/**
 * Check if element is fully visible within the viewport.
 *
 * @since 1.0.0
 *
 * @param  {Object}  el HTMLElement or jQuery Object.
 * @return {Boolean}
 */
export function isElementInViewport(el) {
	// Special bonus for those using jQuery.
	if (typeof jQuery === 'function' && el instanceof jQuery) {
		el = el[0];
	}

	const rect = el.getBoundingClientRect();

	return (rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
	&& rect.right <= (window.innerWidth || document.documentElement.clientWidth));
}

/**
 * Check the userAgent for common touch devices.
 *
 * @since 1.0.1
 */
export function isTouchDevice() {
	return navigator.userAgent.match(/Tablet|iPad|Mobile|Windows Phone|Lumia|Android|webOS|iPhone|iPod|Blackberry|PlayBook|BB10|Opera Mini|\bCrMo\/|Opera Mobi/i) !== null;
}

/**
 * Get the value of a query parameter within a query string.
 *
 * @since 1.2.0
 * @param {string} variable The query param to get.
 */
export function parseQuery(variable) {
	const query = window.location.search.substring(1);
	const vars = query.split('&');
	for (let i = 0; i < vars.length; i++) {
		const pair = vars[i].split('=');
		if (decodeURIComponent(pair[0]) === variable) {
			return decodeURIComponent(pair[1]);
		}
	}

	return false;
}


/**
 * Force images within the provided element to lazy load.
 *
 * @since 1.0.0
 * @param {Node} el The element to search for images in.
 */
export function forceLazyLoad(el) {
	if (typeof jQuery === 'function' && el instanceof jQuery) {
		el.each((i, element) => {
			forceLazyLoad(element);
		});
	} else {
		const images = el.querySelectorAll('img, picture > source');
		if (images) {
			// Force images to load.
			images.forEach((image) => {
				if (image.dataset.src) {
					image.setAttribute('src', image.dataset.src);
					image.classList.add('loaded');
				}
				if (image.dataset.srcset) {
					image.setAttribute('srcset', image.dataset.srcset);
				}
				if (image.dataset.sizes) {
					image.setAttribute('sizes', image.dataset.sizes);
				}
			});
		}
	}
}

/**
 * Cross-browser Element.matches function.
 * @param {Node} el Element to check for matches on.
 * @param {string} selector CSS query selector to check for matches on the passed element.
 */
export function matches(el, selector) {
	return el.matches ? el.matches(selector) : el.msMatchesSelector(selector);
}
